import { Component, OnInit } from '@angular/core';
import { ApiService } from '../../../app/services/api.service';
// --- IMPORTACIÓN DEL SERVICIO DE AUTENTICACIÓN ---
import { AuthenticationService } from '../../security/authentication.service'; 

// --- INTERFACES PARA TIPADO STRICTO ---
interface MainConfig {
  inputIndex: number | null;
  outputIndex: number | null;
  datamodelIndex: number | null;
  formatIndex: number | null;
}

interface SourceOption {
  id: string | number;
  name: string;
  type?: string;
  url?: string;
  provider?: string;
  [key: string]: any; // Para permitir propiedades extra
}

@Component({
  selector: 'app-document-batch',
  templateUrl: './document-batch.component.html',
  styleUrls: ['./document-batch.component.scss']
})
export class DocumentBatchComponent implements OnInit {

  // --- VARIABLES DE ESTADO ---
  public showInputModal: boolean = false;
  public showOutputModal: boolean = false;
  public showScheduleModal: boolean = false;
  public showScheduledRunsModal: boolean = false;

  // --- CONFIGURACIÓN PRINCIPAL ---
  public mainConfig: MainConfig = {
    inputIndex: null,
    outputIndex: null,
    datamodelIndex: null,
    formatIndex: null
  };

  public scheduleData = {
    date: '',
    time: '',
    recurrence: 'ONCE'
  };

  // --- DATOS DINÁMICOS ---
  public scheduledJobsList: any[] = [];
  public processLogs: any[] = [];

  public datamodels: any[] = [];

  // Tipamos los arrays de fuentes
  public inputSources: SourceOption[] = [];
  public outputSources: SourceOption[] = [];

  // --- FORMATOS Y OPCIONES ---
  public currentFormats: string[] = [];
  public formatsConfig: { [key: string]: string[] } = { storage: [], database: [] };
  public sourceOptions: any[] = [];
  public outputSourceOptions: any[] = [];

  // --- VARIABLES FORMULARIOS ---
  public selectedInputSource: any = null;
  public selectedOutputSource: any = null;
  public formValues: any = {};
  public outputFormValues: any = {};

  constructor(
    private apiService: ApiService,
    private authService: AuthenticationService // <--- INYECTADO AQUÍ
  ) { }

  ngOnInit(): void {
    this.loadInitialData();
  }

  // ==============================================================
  // 1. CARGA DE DATOS
  // ==============================================================
  // ==============================================================
  // 1. CARGA DE DATOS
  // ==============================================================
  loadInitialData() {
    console.log("Fetching batch configuration...");
    
    // 1. Guardamos la petición en una variable
    const request$ = this.apiService.get('documentsBatch/batch-config/', '', '', true);

    // 2. Validamos que el ApiService no haya devuelto undefined
    if (request$) {
      request$.subscribe(
        (data: any) => {
          if (data && typeof data === 'object') { 
            
            this.inputSources = data.inputs || [];
            this.outputSources = data.outputs || [];
            this.datamodels = data.datamodels || [];

            if (data.formats) {
              this.formatsConfig = data.formats;
            }

            this.sourceOptions = data.input_options || [];
            this.outputSourceOptions = data.output_options || [];

            this.processLogs = data.logs || [];
            
            // --- FIX DE VISUALIZACIÓN ---
            const rawScheduled = Array.isArray(data.scheduled) ? data.scheduled : [];
            this.scheduledJobsList = rawScheduled.map((job: any) => {
              if (job && job.date && job.time) {
                const utcDateObj = new Date(`${job.date}T${job.time}Z`); 
                if (!isNaN(utcDateObj.getTime())) { 
                  const localDate = utcDateObj.getFullYear() + '-' + 
                                    String(utcDateObj.getMonth() + 1).padStart(2, '0') + '-' + 
                                    String(utcDateObj.getDate()).padStart(2, '0');
                  
                  const localTime = String(utcDateObj.getHours()).padStart(2, '0') + ':' + 
                                    String(utcDateObj.getMinutes()).padStart(2, '0');
                  
                  return { ...job, date: localDate, time: localTime };
                }
              }
              return job || {}; 
            });
            // ---------------------------------
          }
        },
        (error: any) => console.error("Error loading config:", error)
      );
    } else {
      console.error("El ApiService devolvió undefined en lugar de un Observable.");
    }
  }

  // ==============================================================
  // 2. GUARDAR CONFIGURACIÓN
  // ==============================================================

  saveConfiguration() {
    const resourceName = this.formValues.container || this.formValues.bucket || this.formValues.dbName;
    const providerName = this.selectedInputSource ? this.selectedInputSource.name : 'Unknown';
    const finalName = resourceName ? `${providerName} - ${resourceName}` : `New ${providerName}`;

    const newSource = {
      name: finalName,
      provider: this.selectedInputSource?.id,
      type: this.selectedInputSource?.type,
      ...this.formValues
    };

    const payload = { category: 'input', data: newSource };

    this.apiService.post('documentsBatch/save-source', payload, 'Input Source Created').subscribe(
      () => {
        this.loadInitialData();
        this.closeInputModal();
      },
      (err: any) => console.error("Error saving input", err)
    );
  }

  saveOutputConfiguration() {
    const resourceName = this.outputFormValues.container || this.outputFormValues.bucket || this.outputFormValues.dbName || this.outputFormValues.tableName;
    const providerName = this.selectedOutputSource ? this.selectedOutputSource.name : 'Unknown';
    const finalName = resourceName ? `${providerName} - ${resourceName}` : `New ${providerName}`;

    const newSource = {
      name: finalName,
      provider: this.selectedOutputSource?.id,
      type: this.selectedOutputSource?.type,
      ...this.outputFormValues
    };

    const payload = { category: 'output', data: newSource };

    this.apiService.post('documentsBatch/save-source', payload, 'Output Source Created').subscribe(
      (res: any) => {
        this.loadInitialData();
        this.closeOutputModal();
      },
      (err: any) => console.error("Error saving output", err)
    );
  }

  // ==============================================================
  // 3. EJECUCIÓN (Payload Builder)
  // ==============================================================

  private buildJobPayload() {
    const inIdx = this.mainConfig.inputIndex;
    const outIdx = this.mainConfig.outputIndex;
    const dmIdx = this.mainConfig.datamodelIndex;
    const fmtIdx = this.mainConfig.formatIndex;

    const inputName = (inIdx !== null && this.inputSources[inIdx]) ? this.inputSources[inIdx].name : null;
    const outputName = (outIdx !== null && this.outputSources[outIdx]) ? this.outputSources[outIdx].name : null;

    const selectedModel = (dmIdx !== null && this.datamodels[dmIdx]) ? this.datamodels[dmIdx] : null;
    const modelId = selectedModel ? selectedModel.id : null;

    const format = (fmtIdx !== null && this.currentFormats[fmtIdx]) ? this.currentFormats[fmtIdx] : null;

    return {
      config: {
        input: inputName,
        output: outputName,
        datamodel_id: modelId,
        format: format
      }
    };
  }

  runImmediateBatch() {
    if (!this.isConfigValid) return;

    const payload = this.buildJobPayload();
    console.log("🚀 Enviando payload al Endpoint Unificado:", payload);

    this.apiService.post('documentsBatch/start_analysis_batch', payload, 'Procesamiento Iniciado').subscribe(
      (res: any) => {
        console.log("✅ Respuesta del Backend:", res);

        this.processLogs.unshift({
          id: res.process_id || 'PENDING...',
          timestamp: new Date(),
          docsProcessed: 0,
          status: 'PENDING'
        });
      },
      (err: any) => console.error("Error starting batch", err)
    );
  }

  confirmSchedule() {
    const payload: any = this.buildJobPayload();
    
    // --- CONVERSIÓN A UTC ---
    // Unimos la fecha y hora locales del formulario
    const localDateTimeStr = `${this.scheduleData.date}T${this.scheduleData.time}`;
    const localDateObj = new Date(localDateTimeStr);

    // Extraemos la fecha y hora equivalentes en UTC
    const utcDate = localDateObj.toISOString().split('T')[0]; // "YYYY-MM-DD"
    const utcTime = localDateObj.toISOString().split('T')[1].substring(0, 5); // "HH:MM"

    // Armamos el payload con la hora segura
    payload.schedule = { 
      ...this.scheduleData,
      runDate: utcDate, 
      runTime: utcTime  
    };
    // ------------------------

    this.apiService.post('documentsBatch/schedule_batch_job', payload, 'Job Scheduled Successfully').subscribe(
      (response: any) => {
        const dmIdx = this.mainConfig.datamodelIndex;
        const modelName = (dmIdx !== null && this.datamodels[dmIdx]) ? this.datamodels[dmIdx].name : 'Unknown';

        this.scheduledJobsList.push({
          date: this.scheduleData.date, // En la tabla de la UI sí mostramos la hora local para no confundir al usuario
          time: this.scheduleData.time,
          input: payload.config.input,
          output: payload.config.output,
          model: modelName,
          format: payload.config.format,
          recurrence: this.scheduleData.recurrence,
          id: response.jobId
        });
        this.closeScheduleModal();
        this.openScheduledRunsModal();
      },
      (error: any) => console.error("Error scheduling:", error)
    );
  }

  deleteJob(jobId: string) {
    if (!confirm("Are you sure you want to cancel this scheduled job?")) return;

    this.apiService.delete('documentsBatch/scheduled/', jobId, 'Job Cancelled').subscribe(
      () => {
        this.scheduledJobsList = this.scheduledJobsList.filter(job => job.id !== jobId);
      },
      (error: any) => console.error("Error cancelling job", error)
    );
  }

  // ==============================================================
  // UTILIDADES UI
  // ==============================================================

  onOutputChange(index: number | string) {
    const idx = Number(index);

    if (isNaN(idx) || idx < 0) return;

    const selectedSource = this.outputSources[idx];
    this.mainConfig.formatIndex = null; 

    if (!selectedSource || !selectedSource.type) {
      this.currentFormats = [];
      return;
    }

    const type = selectedSource.type;

    this.currentFormats = this.formatsConfig[type]
      || this.formatsConfig[type.toLowerCase()]
      || this.formatsConfig[type.toUpperCase()]
      || [];
  }

  get isConfigValid(): boolean {
    const { inputIndex, outputIndex, datamodelIndex, formatIndex } = this.mainConfig;
    return inputIndex !== null && outputIndex !== null && datamodelIndex !== null && formatIndex !== null;
  }

  openSourceLink(type: 'input' | 'output', index: number | null) {
    if (index === null || index < 0) return;

    let source: SourceOption;
    if (type === 'input') {
      source = this.inputSources[index];
    } else {
      source = this.outputSources[index];
    }

    if (source && source.url && !source.url.startsWith('pgadmin') && !source.url.startsWith('mongodb')) {
      window.open(source.url, '_blank');
    }
  }

  // Modales
  openInputModal() { this.showInputModal = true; this.selectedInputSource = null; this.formValues = {}; }
  closeInputModal() { this.showInputModal = false; }
  selectSource(opt: any) { this.selectedInputSource = opt; this.formValues = {}; }

  openOutputModal() { this.showOutputModal = true; this.selectedOutputSource = null; this.outputFormValues = {}; }
  closeOutputModal() { this.showOutputModal = false; }
  selectOutputSource(opt: any) { this.selectedOutputSource = opt; this.outputFormValues = {}; }

  openScheduleModal() { if (!this.isConfigValid) return; this.showScheduleModal = true; }
  closeScheduleModal() { this.showScheduleModal = false; }

  openScheduledRunsModal() { this.showScheduledRunsModal = true; }
  closeScheduledRunsModal() { this.showScheduledRunsModal = false; }

  onSelectGof(val: any) { }

  // ==============================================================
  // ENRUTAMIENTO DINÁMICO (TENANT)
  // ==============================================================
  getPath(item: string) {
    const tenantName = this.authService.getTenantName();
    return `/${tenantName}/${item}`;
  }
}